/* eslint-disable prettier/prettier */
import { Button } from 'app/components/ui/button';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useProductsSlice } from '../slice';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useToast } from 'app/components/ui/use-toast';
import { Toaster } from 'app/components/ui/toaster';
import { Form } from 'app/components/ui/form';
import ProductForm from '../Form';
import { Save } from 'lucide-react';
import { validateProductForm } from '../Form/validation/productFormSchema';

export default function AddProduct() {
  const form = useForm({
    defaultValues: {
      media: [],
      product_type: 'physical', // Use string instead of number
      variant_mode: 'single', // Set default to single
      tags: [],
      newTags: [],
      categories: [],
      newCategories: [],
      status: 2,
      // Add other default values for stability
      is_digital_download: false,
      requires_activation_key: false,
      requires_shipping_address: true,
      requires_consultation: false,
      is_customizable: false,
      track_inventory: true,
      allow_backorder: false,
      is_featured: false,
      is_active: true,
      digital_file_url: '',
      download_limit: null,
      download_expiry_days: null,
      activation_keys: '',
      digital_delivery_time: '',
      track_digital_stock: false,
      digital_stock_quantity: null,
      // Physical product defaults
      weight: null,
      length: null,
      width: null,
      height: null,
      manufacturer: '',
      supplier_id: null,
      delivery_time: '',
      estimated_delivery_days: null,
      shipping_cost: null,
      free_shipping_threshold: null,
      // Service product defaults
      duration: '',
      service_type: '',
      service_location_type: 'online',
      is_service_available: true,
      min_quantity: null,
      max_quantity: null,
    },
  });
  const { useAddProductsMutation } = useProductsSlice();
  const [addProduct, { isError, isLoading, data }] = useAddProductsMutation();
  const { toast } = useToast();

  // State to track pending variants
  const [pendingVariants, setPendingVariants] = useState<any[]>([]);

  // Callback to handle variant changes from child component
  const handlePendingVariantsChange = (variants: any[]) => {
    setPendingVariants(variants);
  };

  const [searchParams] = useSearchParams();

  const product_type = searchParams.get('product_type');

  const navigate = useNavigate();

  useEffect(() => {
    // Handle URL parameter for product type
    if (product_type) {
      const typeMap = {
        '1': 'digital',
        '2': 'physical',
        '3': 'services'
      };
      const mappedType = typeMap[product_type] || 'physical';
      form.setValue('product_type', mappedType);
    }
  }, [form, product_type]);

  const onSubmit = (data: any) => {
    // Comprehensive validation
    const validationErrors = validateProductForm(data);

    if (validationErrors.length > 0) {
      toast({
        title: 'Validation Error',
        description: validationErrors.join(', '),
        variant: 'destructive',
      });
      return;
    }

    // Include pending variants in the submission if they exist
    const submissionData = {
      ...data,
      ...(pendingVariants.length > 0 && { variants: pendingVariants }),
    };

    // Submit the form
    addProduct(submissionData);
  };

  useEffect(() => {
    if (data) {
      toast({
        title: 'Success',
        description: data?.message || 'Product added successfully',
        variant: 'default',
      });

      // Create pending variants if any exist
      if ((window as any).createPendingVariants && data?.response?.product_id) {
        (window as any).createPendingVariants(data.response.product_id);
      }

      navigate('/products');
    }
  }, [data, toast, navigate]);

  useEffect(() => {
    if (isError) {
      toast({
        title: 'Error',
        description: isError?.message || 'Failed to add product',
        variant: 'destructive',
      });
    }
  }, [isError, toast]);

  return (
    <div className="bg-gray-100 min-h-screen">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <div className="flex justify-between items-center px-4">
            <h3 className="pb-1 text-xl font-semibold">Add Product</h3>

            <div className="flex gap-2">
              <Button type="submit" disabled={isLoading}>
                <Save className="w-4 h-4" />
                {isLoading ? 'Adding...' : 'Add Product'}
              </Button>
            </div>
          </div>
          <ProductForm onPendingVariantsChange={handlePendingVariantsChange} />
        </form>
      </Form>
      <Toaster />
    </div>
  );
}
